import { type Dayjs } from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  applyIndependentRideDateChange,
  errors,
  formatDayjsToTimeHHmm,
  getUTCDateOrNowInISO,
  HubRideActionKind,
  isValidDateTime,
  Log,
  RideFormFields,
  rideStartEndChangeHandler,
  RideStationPanelMode,
  useFormats,
  useSystemParams,
  useTimezone,
  warnAndToastSimpleResult,
  type TRideDtoStartEndKey,
} from "@ryde/shared";

import { useHubRideActionConfirm } from "./hubs-logic";
import { useRideEmptyStationsAllowed } from "./use-ride-empty-stations-allowed";
import { useRidesActions } from "./use-rides-actions";
import { useRidesListActions } from "./use-rides-list-actions";

export const useRideStartEndEdit = (rideId?: number | null) => {
  const { rides } = useRidesListActions();
  const { updateRideStartEndDate } = useRidesActions();
  const { confirmHubRideAction } = useHubRideActionConfirm();
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { timeFormat } = useFormats();
  const { allowShiftTimeOverrideDuringManualAction } = useSystemParams();

  const rideDto = useMemo(
    () => rides?.find(r => r.id === rideId),
    [rideId, rides],
  );

  const { isRideHasIndependentDayAdjustment } = useRideEmptyStationsAllowed(
    RideStationPanelMode.GRID_PANEL,
    rideDto,
  );

  const updateRideStartEnd = useEvent(
    async (valueUtc: Dayjs | null, fieldName: TRideDtoStartEndKey) => {
      if (!rideId || !rideDto) {
        Log.warnAndToast(t(errors.ride.invalidRideId));
        return false;
      }

      if (!isValidDateTime(valueUtc)) {
        Log.warnAndToast(t(errors.ride.invalidRideDate));
        return false;
      }

      const updatedRideDateTimeISO = getUTCDateOrNowInISO(valueUtc, timezone);

      Log.debug(
        "Updating ride date ",
        rideId,
        formatDayjsToTimeHHmm(valueUtc, timezone),
      );

      // Regular behavior: keep start/end consistent via handler
      if (!isRideHasIndependentDayAdjustment) {
        const result = rideStartEndChangeHandler({
          rideDto,
          updatedRideDateTime: updatedRideDateTimeISO,
          rideStartEndKey: fieldName,
          timeFormat,
          timezone,
          allowShiftTimeOverrideDuringManualAction,
        });

        if (result === null || result === "revert") {
          return false;
        }

        const isHubModifyConfirmed = await confirmHubRideAction({
          rides: [rideDto],
          actionKind: HubRideActionKind.Modify,
        });

        if (!isHubModifyConfirmed) {
          return false;
        }

        const dateUpdateResult = await updateRideStartEndDate({
          rideId,
          rideStartDate:
            fieldName === RideFormFields.rideStart
              ? updatedRideDateTimeISO
              : result.newOppositeStartEndRideValue,
          rideEndDate:
            fieldName === RideFormFields.rideEnd
              ? updatedRideDateTimeISO
              : result.newOppositeStartEndRideValue,
          rideTypeId: rideDto?.rideTypeId,
          metadata: {
            ...(rideDto?.metadata || {}),
            ...result.metadataUpdated,
          },
          isReturnDtoForGrid: true,
        });

        if (!dateUpdateResult.success || !dateUpdateResult?.data?.id) {
          warnAndToastSimpleResult(dateUpdateResult, t);
          return false;
        }

        Log.infoAndToast(
          t("ride.RIDE_UPDATED_SUCCESSFULLY"),
          "Date updated successfully",
        );
        return true;
      } else {
        const independentDateChange = applyIndependentRideDateChange({
          rideDto,
          updatedRideDateTime: updatedRideDateTimeISO,
          rideStartEndKey: fieldName,
        });

        const isHubModifyConfirmed = await confirmHubRideAction({
          rides: [rideDto],
          actionKind: HubRideActionKind.Modify,
        });

        if (!isHubModifyConfirmed) {
          return false;
        }

        const dateIndependentUpdateResult = await updateRideStartEndDate({
          rideId,
          rideStartDate: independentDateChange.rideStart,
          rideEndDate: independentDateChange.rideEnd,
          metadata: independentDateChange.metadata,
          rideTypeId: rideDto?.rideTypeId,
          isReturnDtoForGrid: true,
        });

        if (
          !dateIndependentUpdateResult.success ||
          !dateIndependentUpdateResult?.data?.id
        ) {
          warnAndToastSimpleResult(dateIndependentUpdateResult, t);
          return false;
        }

        Log.infoAndToast(
          t("ride.RIDE_UPDATED_SUCCESSFULLY"),
          "Date updated successfully",
        );
        return true;
      }
    },
  );

  return { updateRideStartEnd };
};
